import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Shield, Plus, Pencil, Trash2, Users, Check, X, ChevronDown, ChevronRight, Save, Lock } from 'lucide-react';

const MODULE_GROUPS = {
  'Core': ['dashboard', 'customers', 'reports', 'knowledge_base'],
  'Operations': ['tickets', 'schedule', 'assets', 'alerts'],
  'Billing': ['invoices', 'products_services', 'accounts_receivable', 'accounts_payable', 'ar_settings'],
  'Integrations': ['integrations'],
  'Administration': ['human_resources', 'user_management', 'roles_permissions', 'ticket_settings', 'email_routing', 'email_branding', 'scripts', 'system_settings'],
};

export default function RolesPermissions() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState({});
  const [allPerms, setAllPerms] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingPerms, setEditingPerms] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [reseedingRoles, setReseedingRoles] = useState(false);

  const handleReseedDefaults = async () => {
    setReseedingRoles(true);
    try {
      const res = await api.post('/admin/roles/reseed-defaults');
      const data = res.data;
      if (data.added > 0) {
        toast.success(`Added ${data.added} default role(s)`);
        fetchData();
      } else {
        toast.info('All default roles already exist');
      }
    } catch {
      toast.error('Failed to reset default roles');
    }
    setReseedingRoles(false);
  };
  const [expandedGroups, setExpandedGroups] = useState({});
  const [editingName, setEditingName] = useState(false);
  const [editedRoleName, setEditedRoleName] = useState('');
  const [editedRoleDesc, setEditedRoleDesc] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, permsRes, usersRes] = await Promise.allSettled([
        api.get('/admin/roles'),
        api.get('/admin/roles/permissions'),
        api.get('/users'),
      ]);

      if (rolesRes.status === 'fulfilled') {
        setRoles(rolesRes.value.data);
      } else {
        console.error('Failed to load roles:', rolesRes.reason);
        toast.error('Failed to load roles list');
      }

      if (permsRes.status === 'fulfilled') {
        setModules(permsRes.value.data.modules);
        setAllPerms(permsRes.value.data.all_permissions);
      } else {
        console.error('Failed to load permissions:', permsRes.reason);
      }

      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data);
      } else {
        console.error('Failed to load users:', usersRes.reason);
      }
    } catch (err) {
      console.error('Unexpected error loading roles data:', err);
      toast.error('Failed to load roles data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    // Expand all groups by default
    const expanded = {};
    Object.keys(MODULE_GROUPS).forEach(g => { expanded[g] = true; });
    setExpandedGroups(expanded);
  }, []);

  const selectRole = (role) => {
    setSelectedRole(role);
    setEditingPerms([...role.permissions]);
    setEditingName(false);
    setEditedRoleName(role.name);
    setEditedRoleDesc(role.description);
  };

  const togglePerm = (perm) => {
    setEditingPerms(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const toggleModuleAll = (moduleKey) => {
    const modulePerms = (modules[moduleKey]?.actions || []).map(a => `${moduleKey}:${a}`);
    const allSelected = modulePerms.every(p => editingPerms.includes(p));
    if (allSelected) {
      setEditingPerms(prev => prev.filter(p => !modulePerms.includes(p)));
    } else {
      setEditingPerms(prev => [...new Set([...prev, ...modulePerms])]);
    }
  };

  const toggleGroupAll = (groupKey) => {
    const groupModules = MODULE_GROUPS[groupKey] || [];
    const groupPerms = groupModules.flatMap(m => (modules[m]?.actions || []).map(a => `${m}:${a}`));
    const allSelected = groupPerms.every(p => editingPerms.includes(p));
    if (allSelected) {
      setEditingPerms(prev => prev.filter(p => !groupPerms.includes(p)));
    } else {
      setEditingPerms(prev => [...new Set([...prev, ...groupPerms])]);
    }
  };

  const selectAll = () => setEditingPerms([...allPerms]);
  const deselectAll = () => setEditingPerms([]);

  const savePermissions = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      const updateData = { permissions: editingPerms };
      if (editedRoleName !== selectedRole.name) updateData.name = editedRoleName;
      if (editedRoleDesc !== selectedRole.description) updateData.description = editedRoleDesc;
      await api.put(`/admin/roles/${selectedRole.id}`, updateData);
      toast.success('Role updated successfully');
      await fetchData();
      // Re-select updated role
      const updated = (await api.get('/admin/roles')).data.find(r => r.id === selectedRole.id);
      if (updated) selectRole(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    if (!newRole.name.trim()) { toast.error('Role name is required'); return; }
    try {
      await api.post('/admin/roles', { name: newRole.name, description: newRole.description, permissions: [] });
      toast.success('Role created');
      setShowCreateDialog(false);
      setNewRole({ name: '', description: '' });
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create role');
    }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm('Delete this role? Users assigned to it will lose their permissions.')) return;
    try {
      await api.delete(`/admin/roles/${roleId}`);
      toast.success('Role deleted');
      if (selectedRole?.id === roleId) setSelectedRole(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const assignRole = async (userId, roleId) => {
    try {
      await api.post(`/admin/roles/${roleId}/assign/${userId}`);
      toast.success('Role assigned');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign role');
    }
  };

  const hasChanges = selectedRole && (
    JSON.stringify([...editingPerms].sort()) !== JSON.stringify([...selectedRole.permissions].sort()) ||
    editedRoleName !== selectedRole.name ||
    editedRoleDesc !== selectedRole.description
  );

  const roleUserCount = (roleId) => users.filter(u => u.role_id === roleId).length;
  const adminUserCount = users.filter(u => u.role === 'admin').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="roles-permissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
            <Shield className="w-6 h-6 text-blue-600" />
            Roles & Permissions
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage access control for all CipherOne modules</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {roles.length === 0 && (
            <button
              onClick={handleReseedDefaults}
              disabled={reseedingRoles}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-50 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
              data-testid="reseed-roles-btn"
            >
              <Shield className="w-4 h-4" /> {reseedingRoles ? 'Loading...' : 'Load Default Roles'}
            </button>
          )}
          <button
            onClick={() => setShowAssignDialog(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="assign-roles-btn"
          >
            <Users className="w-4 h-4" /> Assign Roles
          </button>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            data-testid="create-role-btn"
          >
            <Plus className="w-4 h-4" /> New Role
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Roles List */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-700">Roles ({roles.length})</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {roles.length === 0 && (
                <div className="px-4 py-8 text-center text-gray-400">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm font-medium">No roles found</p>
                  <button
                    onClick={handleReseedDefaults}
                    disabled={reseedingRoles}
                    className="mt-3 text-xs text-blue-600 hover:underline"
                    data-testid="reseed-roles-inline-btn"
                  >
                    {reseedingRoles ? 'Loading...' : 'Click to load default roles'}
                  </button>
                </div>
              )}
              {roles.map(role => (
                <button
                  key={role.id}
                  onClick={() => selectRole(role)}
                  className={`w-full text-left px-4 py-3 transition-colors hover:bg-blue-50 ${selectedRole?.id === role.id ? 'bg-blue-50 border-l-3 border-blue-600' : ''}`}
                  data-testid={`role-item-${role.name.toLowerCase()}`}
                  style={selectedRole?.id === role.id ? { borderLeft: '3px solid #2563eb' } : {}}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {role.is_system && <Lock className="w-3 h-3 text-gray-400" />}
                      <span className="text-sm font-medium text-gray-900">{role.name}</span>
                    </div>
                    <span className="text-xs text-gray-400">
                      {role.name === 'Admin' ? adminUserCount : roleUserCount(role.id)} users
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{role.description}</p>
                  <div className="text-xs text-gray-400 mt-1">{role.permissions.length} permissions</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Permissions Editor */}
        <div className="lg:col-span-9">
          {selectedRole ? (
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Role Header */}
              <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1">
                    {editingName ? (
                      <div className="space-y-2">
                        <input
                          value={editedRoleName}
                          onChange={e => setEditedRoleName(e.target.value)}
                          className="text-lg font-bold text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 w-full max-w-xs"
                          data-testid="edit-role-name-input"
                        />
                        <input
                          value={editedRoleDesc}
                          onChange={e => setEditedRoleDesc(e.target.value)}
                          className="text-sm text-gray-600 bg-white border border-gray-300 rounded px-2 py-1 w-full"
                          placeholder="Description"
                          data-testid="edit-role-desc-input"
                        />
                        <button onClick={() => setEditingName(false)} className="text-xs text-blue-600 hover:underline">Done editing</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900">{editedRoleName}</h2>
                        {!selectedRole.is_system && (
                          <button onClick={() => setEditingName(true)} className="text-gray-400 hover:text-gray-600" data-testid="edit-role-name-btn">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!selectedRole.is_system && (
                          <button onClick={() => deleteRole(selectedRole.id)} className="text-red-400 hover:text-red-600 ml-2" data-testid="delete-role-btn">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                    {!editingName && <p className="text-sm text-gray-500">{editedRoleDesc}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={selectAll} className="text-xs px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-100 text-gray-600" data-testid="select-all-btn">
                      Select All
                    </button>
                    <button onClick={deselectAll} className="text-xs px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-100 text-gray-600" data-testid="deselect-all-btn">
                      Clear All
                    </button>
                    <button
                      onClick={savePermissions}
                      disabled={saving || !hasChanges}
                      className={`flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg transition-colors ${hasChanges ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                      data-testid="save-permissions-btn"
                    >
                      <Save className="w-3.5 h-3.5" />
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  {editingPerms.length} of {allPerms.length} permissions selected
                </div>
              </div>

              {/* Permission Matrix */}
              <div className="p-4 sm:p-6 space-y-3" data-testid="permission-matrix">
                {Object.entries(MODULE_GROUPS).map(([groupName, groupModules]) => {
                  const groupPerms = groupModules.flatMap(m => (modules[m]?.actions || []).map(a => `${m}:${a}`));
                  const groupSelected = groupPerms.filter(p => editingPerms.includes(p)).length;
                  const groupTotal = groupPerms.length;
                  const isExpanded = expandedGroups[groupName];

                  return (
                    <div key={groupName} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }))}
                        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                        data-testid={`group-toggle-${groupName.toLowerCase()}`}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                          <span className="text-sm font-semibold text-gray-700">{groupName}</span>
                          <span className="text-xs text-gray-400">({groupSelected}/{groupTotal})</span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleGroupAll(groupName); }}
                          className="text-xs px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
                        >
                          {groupSelected === groupTotal ? 'Deselect Group' : 'Select Group'}
                        </button>
                      </button>

                      {isExpanded && (
                        <div className="divide-y divide-gray-100">
                          {groupModules.filter(m => modules[m]).map(moduleKey => {
                            const mod = modules[moduleKey];
                            const modulePerms = mod.actions.map(a => `${moduleKey}:${a}`);
                            const modSelected = modulePerms.filter(p => editingPerms.includes(p)).length;
                            const allModSelected = modSelected === modulePerms.length;

                            return (
                              <div key={moduleKey} className="flex items-center px-4 py-2.5 hover:bg-gray-50" data-testid={`module-row-${moduleKey}`}>
                                <div className="flex items-center gap-2 w-48 sm:w-64 flex-shrink-0">
                                  <button
                                    onClick={() => toggleModuleAll(moduleKey)}
                                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${allModSelected ? 'bg-blue-600 border-blue-600' : modSelected > 0 ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`}
                                    data-testid={`module-toggle-${moduleKey}`}
                                  >
                                    {allModSelected && <Check className="w-3 h-3 text-white" />}
                                    {!allModSelected && modSelected > 0 && <div className="w-1.5 h-1.5 bg-blue-500 rounded-sm" />}
                                  </button>
                                  <span className="text-sm text-gray-700">{mod.label}</span>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                  {mod.actions.map(action => {
                                    const perm = `${moduleKey}:${action}`;
                                    const isActive = editingPerms.includes(perm);
                                    return (
                                      <button
                                        key={perm}
                                        onClick={() => togglePerm(perm)}
                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${isActive ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                                        data-testid={`perm-${perm}`}
                                      >
                                        {isActive ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                                        {action}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-600">Select a role</h3>
              <p className="text-sm text-gray-400 mt-1">Choose a role from the list to view and edit its permissions</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Role Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="create-role-dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Role</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Name</label>
                <input
                  value={newRole.name}
                  onChange={e => setNewRole(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Field Engineer"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  data-testid="new-role-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <input
                  value={newRole.description}
                  onChange={e => setNewRole(p => ({ ...p, description: e.target.value }))}
                  placeholder="What is this role for?"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  data-testid="new-role-desc-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreateDialog(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50" data-testid="cancel-create-btn">
                Cancel
              </button>
              <button onClick={createRole} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="confirm-create-btn">
                Create Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Roles Dialog */}
      {showAssignDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="assign-roles-dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Assign Roles to Users</h3>
              <button onClick={() => setShowAssignDialog(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-assign-dialog">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Admin users automatically have full access regardless of assigned role.</p>
            <div className="divide-y divide-gray-100">
              {users.filter(u => u.role !== 'customer' && u.role !== 'partner').map(u => {
                const isAdminUser = u.role === 'admin';
                const currentRole = isAdminUser ? 'Admin (built-in)' : roles.find(r => r.id === u.role_id)?.name || 'No role assigned';
                return (
                  <div key={u.id} className="flex items-center justify-between py-3 gap-3" data-testid={`user-row-${u.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.full_name}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isAdminUser ? (
                        <span className="text-xs px-3 py-1 bg-gray-100 text-gray-500 rounded-full">Admin (full access)</span>
                      ) : (
                        <select
                          value={u.role_id || ''}
                          onChange={e => { if (e.target.value) assignRole(u.id, e.target.value); }}
                          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                          data-testid={`assign-select-${u.id}`}
                        >
                          <option value="">-- Select role --</option>
                          {roles.filter(r => r.name !== 'Admin').map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      )}
                      <span className="text-xs text-gray-400 hidden sm:inline w-28 text-right">{currentRole}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
